import { useCompleteOnboardingStep } from '@doneverse/data/hooks'
import { contactsOptions } from '@doneverse/data/queries'
import {
  ONBOARDING_STEP_COPY,
  type OnboardingState,
  type OnboardingStepNumber,
  type OnboardingStepSurface,
} from '@doneverse/shared'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { Check, PartyPopper, Trophy, type LucideIcon } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AgreementSigning } from './AgreementSigning'
import { useOnboardingShell, type OpenOnboardingSurface } from './OnboardingShellContext'
import lock from './OnboardingSurface.module.css'
import styles from './OnboardingTracker.module.css'
import { ProductTour } from './ProductTour'
import { Confetti } from '@/components/shared/Confetti'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOnboarding } from '@/hooks/useOnboarding'
import { useOnboardingLock } from '@/hooks/useOnboardingLock'
import {
  markRunCelebrated,
  readCelebratedRun,
  shouldCelebrateOnboardingFinish,
} from '@/lib/onboardingCelebration'
import { isAllowedWhileOnboardingLocked } from '@/lib/onboardingLock'
import { hasDoerConversation } from '@/lib/productTour'

/**
 * The founder onboarding tracker's SHELL half — ClickUp 86d3uknr9.
 *
 * The tracker the founder reads is `OnboardingPanel`, rendered by `PageLayout`
 * as the first thing on every page. What is left here is everything that must
 * outlive a navigation, mounted once in `IconRailLayout` — the outermost
 * authenticated shell, which the immersive surfaces (Messages, coaching) render
 * inside too:
 *
 *   surfaces      — the agreement document and the product tour. Full-screen,
 *                   because one needs a document's width and the other
 *                   spotlights the shell it is explaining.
 *   celebrations  — the unlock moment (step 5) and the finish (step 11).
 *   minimized pill — the way back to a panel a finished founder put away.
 *
 * Behind the `founder-onboarding` flag: with it off `useOnboarding` returns a
 * null state and this renders nothing at all.
 */

/**
 * What each in-shell surface renders. Keyed by the shared `OnboardingStepSurface`
 * union, so adding a member there fails to compile until it is registered here
 * rather than silently falling through to whichever surface was written last.
 */
const SURFACES: Record<OnboardingStepSurface, (step: OnboardingStepNumber, replay: boolean) => ReactNode> = {
  'product-tour': (step, replay) => <OnboardingProductTour step={step} replay={replay} />,
  agreement: () => <AgreementSigning />,
}

export function OnboardingTracker() {
  const { state, mode, nextUp } = useOnboarding()
  const shell = useOnboardingShell()
  const [celebrating, dismissCelebration] = useUnlockMoment(state ? state.unlocked : null)
  const [finishing, dismissFinish] = useFinishMoment(state)
  useLockedFounderStaysOnTheirStep()

  if (!state || mode === 'hidden') return null

  // The end of the journey outranks its middle: one push can close a whole run
  // (an admin completing everything), and two stacked congratulations would be
  // one too many — so dismissing the finish drops the unlock moment with it.
  //
  // The finish dismisses onto the panel's own finished state — the
  // congratulations card is already on the page behind it. The unlock is the one
  // visible beat between the locked page and the open one, a modal rather than a
  // toast so it lands as an event the founder cannot miss.
  const celebration = finishing ? (
    <OnboardingMoment
      icon={Trophy}
      title="You're all set — save time, get more done! 🎉"
      body="That's every onboarding step done. Your journey stays at the top of the page until you go live."
      cta="Let's get to work"
      // Inside the moment, so it is torn down with the moment it belongs to
      // rather than left running behind the shell.
      confetti
      // Dismisses onto the panel's finished card, which carries the "Go Live"
      // button that actually closes the flow (ClickUp 86d40tuc8). Deliberately
      // NOT putting the panel away here as well: that would make this button and
      // that one two different-looking ways to do the same thing, and a founder
      // who dismissed the confetti would never see the card they are meant to
      // press through.
      onDismiss={() => {
        dismissFinish()
        dismissCelebration()
      }}
    />
  ) : celebrating ? (
    <OnboardingMoment
      icon={PartyPopper}
      title="Your Dashboard is now fully unlocked!"
      body="Everything is open now. Your next steps are waiting at the top of the page."
      cta="Explore your dashboard"
      onDismiss={dismissCelebration}
    />
  ) : null

  return (
    <>
      {celebration}
      {renderOpenSurface(shell?.surface ?? null, nextUp)}
      {mode === 'minimized' && shell?.panelDismissed && (
        <button type="button" className={styles.minimizedPill} onClick={() => shell.restorePanel()}>
          <Check className={styles.pillIcon} aria-hidden />
          Onboarding complete
        </button>
      )}
    </>
  )
}

/**
 * Move a stranded founder off a page that has stopped being theirs.
 *
 * The router enforces the allowlist on NAVIGATION, which covers every way a
 * founder can ASK for a page — but not the one way a page stops being allowed
 * while they stand on it: the step that opened it closing underneath them.
 *
 * Usually that needs no rescue, because the panel is right there on the page
 * saying what is next — and moving them would cost them something they earned,
 * like the "Booked ✓ — see you on the 14th" they just watched appear. So this
 * fires only where there is no panel to read: the immersive surfaces that skip
 * `PageLayout` (see `panelMounted`). The intake is the case that matters — a
 * founder who answered the last of 41 questions would otherwise sit on a
 * finished conversation with no onward anything.
 *
 * Sends them where the router would: `redirectTo`, which is the active step's
 * own surface or the founder home, and is always itself allowed — so this can
 * settle but never loop.
 */
function useLockedFounderStaysOnTheirStep(): void {
  const lock = useOnboardingLock()
  const shell = useOnboardingShell()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const panelMounted = shell?.panelMounted ?? false

  useEffect(() => {
    if (panelMounted) return
    if (isAllowedWhileOnboardingLocked(pathname, lock)) return
    // `replace`, so Back does not walk them into the page they just left.
    navigate({ to: lock.redirectTo, replace: true })
  }, [pathname, lock, navigate, panelMounted])
}

/**
 * The open surface, drawn for the step it belongs to — or nothing.
 *
 * For a surface opened as the active step the step is matched against THIS
 * step's surface, not just "something is open": a webhook completing the step
 * under the founder advances `nextUp`, and a stale flag would otherwise hold
 * step 4's tour up over step 5. That match failing IS the teardown.
 *
 * A replay has no active step to match — its step is already complete — so it
 * resolves from the surface itself and stays until it closes itself.
 */
function renderOpenSurface(
  open: OpenOnboardingSurface | null,
  nextUp: OnboardingStepNumber | null,
): ReactNode {
  if (open === null) return null

  const step = open.replay
    ? findStepForSurface(open.surface)
    : nextUp !== null && ONBOARDING_STEP_COPY[nextUp].surface === open.surface
      ? nextUp
      : null

  return step === null ? null : SURFACES[open.surface](step, open.replay)
}

/** The step a surface belongs to — the reverse of `ONBOARDING_STEP_COPY[n].surface`. */
function findStepForSurface(surface: OnboardingStepSurface): OnboardingStepNumber | null {
  const entry = Object.entries(ONBOARDING_STEP_COPY).find(([, copy]) => copy.surface === surface)
  return entry ? (Number(entry[0]) as OnboardingStepNumber) : null
}

/**
 * Whether to play the unlock moment right now.
 *
 * Fires on the false → true edge of `unlocked` *within this mount*, which is
 * exactly "the founder was here when it happened" — through their own step-4
 * click, or through a webhook completing step 3 while they watched. It never
 * fires on the first render, so a reload mid-transition lands on the unlocked
 * shell with the panel rather than replaying the celebration or getting stuck
 * half-way: the state is the server's, and this is only the animation of the
 * change.
 *
 * Deliberately not persisted. A "have we shown this yet" flag in storage would
 * be a second, client-owned source of truth for something the server already
 * knows, and it would survive a reset (Onboarding 12) into a run where the
 * founder genuinely has not been unlocked yet.
 *
 * `null` means "no tracker for this user" — a non-founder, the grandfathered
 * cohort, or anyone outside the `founder-onboarding` rollout. It is NOT the same
 * as "locked", and collapsing the two would fire the celebration at an
 * already-unlocked founder the moment the flag was switched on for them.
 */
function useUnlockMoment(unlocked: boolean | null): [boolean, () => void] {
  const [celebrating, setCelebrating] = useState(false)
  const previous = useRef<boolean | null>(null)

  useEffect(() => {
    if (unlocked === null) return
    if (previous.current === false && unlocked) setCelebrating(true)
    previous.current = unlocked
  }, [unlocked])

  return [celebrating, () => setCelebrating(false)]
}

/**
 * Whether to throw the finishing party right now — step 11, "Congratulations".
 *
 * Unlike {@link useUnlockMoment} this is NOT an edge inside one mount, and the
 * difference is the reason it is a second hook rather than a second call of the
 * first. Step 5 follows the founder's own click, so they are always there to see
 * it; step 11 follows step 10, which closes on a launch date or an admin event
 * that may land while nobody is looking. So the question here is "is this
 * founder freshly finished, and has this browser already thrown the party" —
 * see `lib/onboardingCelebration.ts` for what that costs and why the one bit
 * that is stored is keyed by run.
 *
 * Marking it celebrated as it opens (rather than on dismiss) is deliberate: a
 * founder who closes the tab mid-confetti has already had the moment, and the
 * alternative is a celebration that reappears on every page load until it is
 * formally acknowledged.
 */
function useFinishMoment(state: OnboardingState | null): [boolean, () => void] {
  const { profile } = useAuth()
  const userId = profile?.id ?? null
  const [finishing, setFinishing] = useState(false)

  useEffect(() => {
    if (!userId || !state) return
    if (!shouldCelebrateOnboardingFinish(state, readCelebratedRun(userId), Date.now())) return
    markRunCelebrated(userId, state.runNumber)
    setFinishing(true)
  }, [userId, state])

  return [finishing, () => setFinishing(false)]
}

/**
 * A celebration: the unlock (step 5) and the finish (step 11).
 *
 * They wear the same surface as the locked overlay (`OnboardingSurface.module.css`)
 * but are DISMISSIBLE, which is the whole difference and the reason this is a
 * sibling of `OnboardingLockCard` rather than a flag on it: there is nothing
 * left to gate at either moment, and a lock card that could be talked out of
 * locking is the failure mode that component's doc warns about. Written once so
 * the Radix contract underneath — the modal root, the `asChild` title and
 * description that give the dialog its accessible name — cannot drift between
 * the two beats.
 */
function OnboardingMoment({
  icon: Icon,
  title,
  body,
  cta,
  confetti = false,
  onDismiss,
}: {
  icon: LucideIcon
  title: string
  body: string
  cta: string
  confetti?: boolean
  onDismiss: () => void
}) {
  return (
    <DialogPrimitive.Root open modal onOpenChange={(open) => !open && onDismiss()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={lock.overlay}>
          {confetti && <Confetti />}
          <DialogPrimitive.Content className={lock.card}>
            <Icon className={lock.icon} aria-hidden />
            <DialogPrimitive.Title asChild>
              <h1 className={lock.title}>{title}</h1>
            </DialogPrimitive.Title>
            <DialogPrimitive.Description asChild>
              <p className={lock.body}>{body}</p>
            </DialogPrimitive.Description>
            <Button size="lg" onClick={onDismiss}>
              {cta}
            </Button>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/**
 * The product tour, plus the completion it reports.
 *
 * Its own component so the step-4 mutation lives beside the surface that
 * triggers it rather than in the tracker's top-level render, and so the
 * contacts query — the tour's "does this founder have anyone to message yet"
 * input — is only issued once the tour is actually open.
 *
 * Finishing and skipping are the same call. Step 4 asserts "the founder has
 * been shown around"; a founder who skipped the tour has still been offered it,
 * and holding the dashboard hostage to seven Next clicks would be a worse
 * product than trusting them. `metadata` keeps the difference for analytics
 * (Onboarding 13) without making it a gate.
 *
 * There is no local "finished" flag on the first run: the tour stays mounted
 * until the SERVER says step 4 is closed, at which point the tracker stops
 * matching it and this unmounts straight into the unlock moment. That is also
 * what makes a failed call recoverable — `isPending` drops, the controls come
 * back, and the founder can try again on the surface they never left. A replay
 * has no such edge to wait on (the step was already complete), so it is the one
 * case that closes itself.
 */
function OnboardingProductTour({ step, replay }: { step: OnboardingStepNumber; replay: boolean }) {
  const { hasFlag } = useFeatureFlags()
  const shell = useOnboardingShell()
  const complete = useCompleteOnboardingStep()
  // The rail already fetches this for its unread badge, so on the shell this
  // runs in it is a cache read rather than a request.
  const { data: contacts } = useQuery(contactsOptions())

  return (
    <ProductTour
      context={{ hasFlag, hasDoerContact: hasDoerConversation(contacts ?? []) }}
      finishing={complete.isPending}
      onFinish={({ skipped, stopsSeen }) => {
        if (replay) {
          // Nothing to report: the step is already closed, and re-completing it
          // would be a second completion event for one thing that happened.
          shell?.closeSurface()
          return
        }
        complete.mutate({ step, metadata: { source: 'product_tour', skipped, stopsSeen } })
      }}
    />
  )
}
